// ImageModal.tsx

import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faAnglesLeft,
  faAnglesRight,
} from "@fortawesome/free-solid-svg-icons";
import { customYellowColor } from "utils/colors";
import { isBase64 } from "utils/imagesAndColors";

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  selectedIndex: number;
  onSelectImage: (index: number) => void;
  isReadOnly?: boolean;
  isSingleImage?: boolean;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  images,
  selectedIndex,
  onSelectImage,
  isReadOnly = false,
  isSingleImage = false,
}) => {
  /**
   * Effect hook to handle keyboard navigation in the image modal
   * @effect
   * @dependencies [isOpen, onClose, selectedIndex, images.length]
   *
   * Manages keyboard events:
   * - Escape: Closes the modal
   * - ArrowLeft: Shows previous image
   * - ArrowRight: Shows next image
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowLeft") {
        handlePrevImage();
      } else if (event.key === "ArrowRight") {
        handleNextImage();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, selectedIndex, images?.length]);

  /**
   * Early return if modal is not open
   */
  if (!isOpen) return null;

  /**
   * Handles navigation to the previous image
   * Uses modulo operator to wrap around to the last image when at the beginning
   */
  const handlePrevImage = () => {
    onSelectImage((selectedIndex - 1 + images?.length) % images?.length);
  };

  /**
   * Handles navigation to the next image
   * Uses modulo operator to wrap around to the first image when at the end
   */
  const handleNextImage = () => {
    onSelectImage((selectedIndex + 1) % images?.length);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{ position: "absolute", top: 20, right: 20, cursor: "pointer" }}
      >
        <FontAwesomeIcon
          icon={faTimes}
          color={customYellowColor}
          size="2x"
          onClick={onClose}
        />
      </div>
      <div style={{ position: "relative", width: "80%", height: "70%" }}>
        <img
          src={
            isBase64(images?.[selectedIndex])
              ? `data:image/jpeg;base64,${images?.[selectedIndex]}`
              : images?.[selectedIndex]
          }
          alt="Selected"
          style={{ width: "100%", height: "100%", objectFit: "contain", paddingTop: "4rem" }}
        />
        {!isSingleImage && (
          <>
            <FontAwesomeIcon
              icon={faAnglesLeft}
              color={customYellowColor}
              size="8x"
              style={{
                position: "absolute",
                left: -50,
                top: "50%",
                cursor: "pointer",
              }}
              onClick={handlePrevImage}
            />
            <FontAwesomeIcon
              icon={faAnglesRight}
              color={customYellowColor}
              size="8x"
              style={{
                position: "absolute",
                right: -50,
                top: "50%",
                cursor: "pointer",
              }}
              onClick={handleNextImage}
            />
          </>
        )}
      </div>
      {!isSingleImage && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "5rem",
            height: "15rem",
          }}
        >
          {images?.map((image, index) => {
            const distance = Math.abs(index - selectedIndex);
            const scale = distance === 0 ? 1 : 1 - distance * 0.1;
            const translateY = distance === 0 ? -30 : -15 + distance * 5;
            const zIndex = images?.length - distance;

            return (
              <div
                key={image}
                style={{
                  width: "10rem",
                  height: "10rem",
                  margin: "0 5px",
                  marginTop: "3rem",
                  cursor: "pointer",
                  transform: `translateY(${translateY}px) scale(${scale})`,
                  transition: "all 0.5s",
                  borderRadius: "9px",
                  position: "relative",
                  zIndex: zIndex,
                }}
                onClick={() => onSelectImage(index)}
              >
                <img
                  src={isReadOnly ? image : `data:image/jpeg;base64,${image}`}
                  alt={`Thumbnail ${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "9px",
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
      ;
    </div>
  );
};

export default ImageModal;
